import dayjs from 'dayjs';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getBookingActivity, getBookingById } from 'services';
import { Booking, BookingDetails, Role } from 'types';

import { DataTable, Dialog, Loader, useDialog } from 'components';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import { useAuth } from 'contexts';
import { BookingActivityDisplay } from 'types/audit/booking-activity';
import { getIsoDisplay } from 'utils';
import { transformBookingActivity } from 'utils/booking-activity';
import { BookingTransactions, SaveTemplateDialog } from '.';
import { bookingActivityDataColumns } from './booking-activity-columns';
import './booking-details.scss';
import BookingTruckingCompanies from './booking-trucking-companies';
import BookingUnits from './booking-units';

const BookingDetailsRow: FC<BookingDetails> = ({ carb, count, delivered, iso, received, reserved }) => (
  <div className="grid-6 p-2">
    <div>
      <h6 className="text-dark font-weight-regular">SIZE TYPE</h6>
      <p className="text-black">{!!iso ? getIsoDisplay(iso) : 'N/A'}</p>
    </div>
    <div>
      <h6 className="text-dark font-weight-regular">QUANTITY</h6>
      <p className="text-black">{count ?? 'N/A'}</p>
    </div>
    <div>
      <h6 className="text-dark font-weight-regular">RECEIVED</h6>
      <p className="text-black">{received ?? 0}</p>
    </div>
    <div>
      <h6 className="text-dark font-weight-regular">RESERVED</h6>
      <p className="text-black">{reserved ?? 0}</p>
    </div>
    <div>
      <h6 className="text-dark font-weight-regular">DELIVERED</h6>
      <p className="text-black">{delivered ?? 0}</p>
    </div>
    <div>
      <h6 className="text-dark font-weight-regular margin-right-8px">CARB</h6>
      <p className="text-black">{typeof carb !== 'boolean' ? 'N/A' : carb ? 'Yes' : 'No'}</p>
    </div>
  </div>
);

const ViewBooking: FC = () => {
  const navigate = useNavigate();
  const params = useParams();

  const { userProfile } = useAuth();

  const [booking, setBooking] = useState<Booking | null>();

  const loadBooking = useCallback(async () => {
    const id = Number(params.bookingId);
    if (!isNaN(id)) setBooking(await getBookingById(id));
  }, [params.bookingId, setBooking]);

  useEffect(() => {
    loadBooking();
  }, [loadBooking]);

  /* booking activities */
  const {
    ref: viewActivityDialogRef,
    openDialog: openViewActivityDialog,
    closeDialog: closeViewActivityDialog
  } = useDialog({});

  /* template dialog */
  const {
    ref: saveTemplateDialogRef,
    openDialog: openSaveTemplateDialog,
    closeDialog: closeSaveTemplateDialog
  } = useDialog({ onOpen: () => console.log('test') });

  const loadBookingActivities = useCallback(async () => {
    if (!booking?.id || isNaN(booking.id)) {
      return [];
    }

    const activities = await getBookingActivity(booking.id);
    const transforms: BookingActivityDisplay[] = [];

    for (const [index, activity] of activities.entries()) {
      transforms.push(
        index > 0 ? transformBookingActivity(activity, activities.at(index - 1)) : transformBookingActivity(activity)
      );
    }

    return transforms.reverse();
  }, [booking?.id]);

  return (
    <div className="page" id="booking-details">
      <div className="page-header">
        <h1>BOOKING #{booking?.bookingNumber ?? 'N/A'}</h1>
        {booking === null && (
          <div className="font-weight-bold px-3 py-2 justify-self-center text-white" id="inactive-badge">
            NOT FOUND
          </div>
        )}
        <div className="d-flex g-3 flex-row-reverse">
          {[Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING, Role.BOOKING].includes(userProfile.role) && (
            <button className="btn-primary text-uppercase" onClick={openViewActivityDialog}>
              View Log
            </button>
          )}
          {[
            Role.ADMIN,
            Role.BILLING_ADMIN,
            Role.ADMIN_CUSTOMER,
            Role.CUSTOMER,
            Role.SENIOR_BOOKING,
            Role.BOOKING,
            Role.GATE_BOOKING,
            Role.EXTERNAL
          ].includes(userProfile.role) && (
            <button className="btn-primary" onClick={() => navigate('edit', { replace: true })} disabled={!booking}>
              EDIT
            </button>
          )}
          {/* templates are denied to GATE_BOOKING by the API */}
          {userProfile.role !== Role.GATE_BOOKING && (
            <button className="btn-outline" onClick={openSaveTemplateDialog} disabled={!booking}>
              SAVE AS TEMPLATE
            </button>
          )}
          <button className="btn-back" onClick={() => navigate(-1)}>
            <span className="material-icons">arrow_back_ios</span>
            BACK
          </button>
        </div>
      </div>
      <Dialog width="80vw" ref={viewActivityDialogRef} closeDialog={closeViewActivityDialog}>
        <div className="dialog-header">
          <h3>Activity Log</h3>
          <button className="btn-close" onClick={closeViewActivityDialog}>
            <span className="material-icons">close</span>
          </button>
        </div>
        <div className="dialog-body">
          <DataTable
            tableClass="activity-log"
            fetchData={loadBookingActivities}
            columns={bookingActivityDataColumns}
            initialPage={{ pageNum: 0, pageSize: 25, sortField: 'date', sortDirection: 'desc' }}
            initialColumns={['timestamp', 'action', 'modifiedBy', 'description'].map(c => {
              return { field: c };
            })}
            clientSidePagination
            hideTableControls
          />
        </div>
      </Dialog>
      <div className="page-content">
        <Loader loading={booking === undefined}>
          <div className="card-primary">
            <div className="card-header px-3">
              <h4>GENERAL INFORMATION</h4>
            </div>
            <div className="card-body">
              <div className="grid-6 p-2">
                <div>
                  <h6 className="text-dark font-weight-regular">BOOKING NUMBER</h6>
                  <p className="text-black">{booking?.bookingNumber ?? 'N/A'}</p>
                </div>
                <div>
                  <h6 className="text-dark font-weight-regular">ACCOUNT</h6>
                  <p className="text-black">{booking?.customer?.name ?? 'N/A'}</p>
                </div>
                <div>
                  <h6 className="text-dark font-weight-regular">LOCATION</h6>
                  <p className="text-black">{booking?.location?.name ?? 'N/A'}</p>
                </div>
                <div>
                  <h6 className="text-dark font-weight-regular">RAILYARD</h6>
                  <p className="text-black">{booking?.railyard}</p>
                </div>
                <div>
                  <h6 className="text-dark font-weight-regular">START DATE</h6>
                  <p className="text-black">
                    {dayjs
                      .utc(booking?.startDate ?? booking?.createdDate)
                      .local()
                      .format('l')}
                  </p>
                </div>
                <div>
                  <h6 className="text-dark font-weight-regular">EXPIRATION DATE</h6>
                  <p className="text-black">
                    {!booking?.expirationDate ? 'N/A' : dayjs.utc(booking.expirationDate).local().format('l')}
                  </p>
                </div>
              </div>
              <div className="grid-6 p-2">
                <div>
                  <h6 className="text-dark font-weight-regular">SSL SCAC</h6>
                  <p className="text-black">{booking?.sslScac ?? ''}</p>
                </div>
                <div>
                  <h6 className="text-dark font-weight-regular">CREATED BY</h6>
                  {booking?.createdBy === 'STEDI' ? (
                    <a
                      href={'https://www.stedi.com/app/core/file-executions/' + (booking.fileExecutionId ?? '')}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      STEDI
                    </a>
                  ) : (
                    <p className="text-black">{booking?.createdBy ?? ''}</p>
                  )}
                </div>
                <div className="col-span-4">
                  <div>
                    <h6 className="text-dark font-weight-regular">NOTES</h6>
                    <p className="text-black">{booking?.notes ?? 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!booking?.bookingDetails?.length ? null : (
            <div className="card-primary">
              <div className="card-header px-3">
                <h4>EQUIPMENT DETAILS</h4>
              </div>
              <div className="card-body">
                {booking.bookingDetails.map((details, i) => (
                  <BookingDetailsRow key={i} {...details} />
                ))}
              </div>
            </div>
          )}
          {booking?.bookingDetails?.some(
            ({ iso }) => iso && iso.reduce((acc, curr) => acc || /\d\d(?:HR|R[CFST]|VT)/.test(curr), false)
          ) && (
            <div className="card-primary">
              <div className="card-header px-3">
                <h4>REEFER DETAILS</h4>
              </div>
              <div className="card-body">
                <div className="grid-4 p-2">
                  <div>
                    <h6 className="text-dark font-weight-regular">BOOKING TEMPERATURE</h6>
                    <p className="text-black">
                      {typeof booking?.bookingTemperature === 'number' ? `${booking.bookingTemperature}°C` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <h6 className="text-dark font-weight-regular">HUMIDITY</h6>
                    <p className="text-black">
                      {typeof booking?.humidity === 'number' ? `${booking.humidity}%` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <h6 className="text-dark font-weight-regular">VENTILATION</h6>
                    <p className="text-black">{booking.ventilation}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <BookingUnits {...booking} />
          <BookingTransactions />
          <BookingTruckingCompanies truckingCompanies={booking?.truckingCompanies} />
        </Loader>
      </div>
      <SaveTemplateDialog
        booking={{ ...booking, id: undefined }}
        closeDialog={closeSaveTemplateDialog}
        ref={saveTemplateDialogRef}
      />
      <ErrorDialog />
      <SuccessDialog />
    </div>
  );
};
export default ViewBooking;
